// A stored secret's settings: its name, where the broker may use it, an HTTP API key's hosts
// and header, and whether the agent may read it, saved as one device-signed update. The value
// is never shown; "Replace value" switches the drawer to the replace form.
import { ArrowsClockwise } from "@phosphor-icons/react";
import { type FormEvent, useState } from "react";
import { Button, Input } from "rizzui";

import { useT } from "../i18n";
import { updateSecret } from "./api";
import { editProblem, editStart, hasInjection, type SecretEdit, updatePayload } from "./edit";
import type { Signer } from "./requests";
import { DrawerBody, DrawerFooter, Hinted } from "./SecretDrawer";
import { AgentReadable } from "./SecretOptions";
import { type Credential, templateOf } from "./templates";

interface Props {
  signer: Signer;
  signerName: string;
  credential: Credential;
  onSaved: (credential: Credential) => void;
  onError: (message: string) => void;
  onReplace: () => void;
  onCancel: () => void;
}

export function EditSecretForm({ signer, signerName, credential, onSaved, onError, onReplace, onCancel }: Props) {
  const { common, secrets } = useT();
  const add = secrets.forms.add;
  const copy = secrets.forms.edit;
  const template = templateOf(credential);
  const [edit, setEdit] = useState<SecretEdit>(() => editStart(credential, template));
  const [busy, setBusy] = useState(false);
  const set = (patch: Partial<SecretEdit>) => setEdit((e) => ({ ...e, ...patch }));

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const problem = editProblem(credential, template, edit);
    if (problem) {
      onError(problem);
      return;
    }
    setBusy(true);
    try {
      onSaved(await updateSecret(signer, credential.id, updatePayload(credential, template, edit)));
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="flex min-h-0 flex-1 flex-col" aria-label={copy.form(credential.name)} noValidate onSubmit={(e) => void onSubmit(e)}>
      <DrawerBody>
        <Hinted hint={add.nameHint}>
          <Input label={add.name} value={edit.name} onChange={(e) => set({ name: e.target.value })} maxLength={128} />
        </Hinted>
        {template?.scope ? (
          <Hinted hint={add.scopeHint}>
            <Input
              label={add.scopeLabel(template.scope.label)}
              value={edit.scope}
              placeholder={template.scope.placeholder}
              onChange={(e) => set({ scope: e.target.value })}
            />
          </Hinted>
        ) : null}
        {hasInjection(credential, template) ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label={add.hosts} value={edit.hosts} placeholder="api.stripe.com" onChange={(e) => set({ hosts: e.target.value })} />
            <Input label={add.header} value={edit.header} onChange={(e) => set({ header: e.target.value })} inputClassName="font-mono text-xs" />
          </div>
        ) : null}
        <section className="rounded-xl border border-muted p-3.5" aria-label={copy.valueTitle}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="mb-0 text-[13px] font-semibold text-gray-900">{copy.valueTitle}</p>
              <p className="mb-0 mt-0.5 text-xs text-gray-500">{copy.valueBody}</p>
            </div>
            <Button size="sm" variant="outline" className="gap-1.5" onClick={onReplace} disabled={busy}>
              <ArrowsClockwise weight="bold" className="h-3.5 w-3.5" /> {copy.replaceValue}
            </Button>
          </div>
        </section>
        <AgentReadable checked={edit.workerExposed} onChange={(workerExposed) => set({ workerExposed })} />
      </DrawerBody>
      <DrawerFooter signedBy={signerName}>
        <Button variant="outline" onClick={onCancel} disabled={busy}>
          {common.actions.cancel}
        </Button>
        <Button type="submit" isLoading={busy}>
          {copy.save}
        </Button>
      </DrawerFooter>
    </form>
  );
}
